import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

const dimensionLabels = {
  county: 'County',
  gender: 'Gender',
  age: 'Age',
  urban_rural: 'Urban/rural',
};

function formatHours(value) {
  const hours = Number(value);
  if (!Number.isFinite(hours)) return '—';
  if (hours < 48) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const remaining = Math.round(hours - days * 24);
  return remaining ? `${days}d ${remaining}h` : `${days}d`;
}

function ClientsActivationBreakdown({
  response,
  status,
  dimension,
  onDimensionChange,
}) {
  const [showAll, setShowAll] = React.useState(false);
  const loading = status === 'loading';
  const windowDays = Number(response?.filters?.windowDays || 7);
  const rows = (response?.breakdowns?.[dimension] || [])
    .filter((row) => Number(row.registrations || 0) > 0);
  const visibleRows = showAll ? rows : rows.slice(0, 10);

  React.useEffect(() => {
    setShowAll(false);
  }, [dimension]);

  return (
    <Card
      sx={{
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack spacing={1.25}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={1}
          >
            <Box>
              <Typography sx={{ color: '#111827', fontWeight: 800, fontSize: '1.02rem' }}>
                Purchase activation breakdown
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.76rem', mt: 0.15 }}>
                First purchase within {windowDays} days · eligible clients shown
              </Typography>
            </Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={dimension}
              onChange={onDimensionChange}
              aria-label="Activation breakdown dimension"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, auto)' },
                '& .MuiToggleButton-root': {
                  px: 1.15,
                  py: 0.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                },
              }}
            >
              {Object.entries(dimensionLabels).map(([value, label]) => (
                <ToggleButton key={value} value={value}>{label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <Box
            sx={{
              display: { xs: 'none', sm: 'grid' },
              gridTemplateColumns: 'minmax(150px, 1fr) 90px 90px 110px',
              gap: 1,
              px: 1,
              color: '#64748b',
              fontSize: '0.68rem',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            <Box>Segment</Box>
            <Box textAlign="right">Activation</Box>
            <Box textAlign="right">Eligible</Box>
            <Box textAlign="right">Median</Box>
          </Box>

          {loading && !response ? (
            <Stack spacing={0.75}>
              {[0, 1, 2, 3].map((key) => (
                <Skeleton key={key} variant="rounded" height={48} />
              ))}
            </Stack>
          ) : visibleRows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: '12px' }}>
              <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                No activation data for this breakdown.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0.6}>
              {visibleRows.map((row) => (
                <Box
                  key={`${dimension}-${row.value || row.key}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr auto', sm: 'minmax(150px, 1fr) 90px 90px 110px' },
                    gap: { xs: 0.35, sm: 1 },
                    alignItems: 'center',
                    px: 1,
                    py: 0.8,
                    bgcolor: '#f8fafc',
                    borderRadius: '10px',
                  }}
                >
                  <Box minWidth={0}>
                    <Typography noWrap sx={{ color: '#0f172a', fontWeight: 750, fontSize: '0.8rem' }}>
                      {row.label}
                    </Typography>
                    <Typography sx={{ display: { xs: 'block', sm: 'none' }, color: '#64748b', fontSize: '0.68rem' }}>
                      {Number(row.eligible || 0).toLocaleString('ro-RO')} eligible · median {formatHours(row.median_hours)}
                    </Typography>
                  </Box>
                  <Typography sx={{ textAlign: 'right', color: '#1d4ed8', fontWeight: 800, fontSize: '0.84rem' }}>
                    {row.rate === null || typeof row.rate === 'undefined'
                      ? '—'
                      : `${Number(row.rate).toFixed(1)}%`}
                  </Typography>
                  <Typography sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', color: '#334155', fontSize: '0.78rem' }}>
                    {Number(row.eligible || 0).toLocaleString('ro-RO')}
                  </Typography>
                  <Typography sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', color: '#334155', fontSize: '0.78rem' }}>
                    {formatHours(row.median_hours)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          {rows.length > 10 && (
            <Button
              size="small"
              onClick={() => setShowAll((value) => !value)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
            >
              {showAll ? 'Show top 10' : `Show all ${rows.length}`}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default ClientsActivationBreakdown;
